import React, { useCallback, useRef, useState } from 'react';
import { Box, LinearProgress, Paper, Stack, Tab, Tabs, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { MarkdownRenderer } from 'Components/MarkdownRenderer';
import { ProductExampleAttachmentDto } from 'apiClient/Api';
import { buildAttachmentRef } from 'Hooks/useAttachmentTokens';

interface MarkdownEditorProps {
  value: string;
  onChange: (value: string) => void;
  exampleId?: string | null;
  attachments?: ProductExampleAttachmentDto[];
  /** Returns the uploaded attachment (for inline-ref insertion). Throws on failure. */
  onUpload?: (file: File) => Promise<ProductExampleAttachmentDto | null>;
  minRows?: number;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
}

const isImage = (file: File): boolean => file.type.startsWith('image/');

/**
 * Split editor with a Markdown source pane and a live preview. Handles
 * drag/drop and paste of files — on drop the file is uploaded via the
 * provided callback and the returned attachment id is inserted as
 * `![name](attachment:{id})` (images) or `[name](attachment:{id})` at the
 * current cursor position.
 */
export const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value,
  onChange,
  exampleId,
  attachments,
  onUpload,
  minRows = 14,
  placeholder,
  label,
  disabled,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, setTab] = useState<'source' | 'preview'>('source');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const insertAtCaret = useCallback((snippet: string) => {
    const el = textareaRef.current;
    if (!el) {
      onChange(value ? `${value}\n${snippet}` : snippet);
      return;
    }
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const next = value.slice(0, start) + snippet + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      const caret = start + snippet.length;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(caret, caret);
    });
  }, [onChange, value]);

  const uploadFile = useCallback(async (file: File) => {
    if (!onUpload) return;
    setUploading(true);
    try {
      const att = await onUpload(file);
      if (!att?.id) return;
      const safeName = (att.fileName ?? file.name).replace(/[[\]]/g, '');
      const ref = buildAttachmentRef(att.id);
      const snippet = isImage(file)
        ? `\n\n![${safeName}](${ref})\n\n`
        : `[${safeName}](${ref})`;
      insertAtCaret(snippet);
    } finally {
      setUploading(false);
    }
  }, [insertAtCaret, onUpload]);

  const handleDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    if (!onUpload || disabled) return;
    const files = Array.from(event.dataTransfer.files);
    for (const file of files) {
      await uploadFile(file);
    }
  }, [disabled, onUpload, uploadFile]);

  const handlePaste = useCallback(async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!onUpload || disabled) return;
    const items = Array.from(event.clipboardData?.items ?? []);
    const fileItems = items.filter((it) => it.kind === 'file');
    if (fileItems.length === 0) return;
    event.preventDefault();
    for (const item of fileItems) {
      const file = item.getAsFile();
      if (file) await uploadFile(file);
    }
  }, [disabled, onUpload, uploadFile]);

  const sourcePane = (
    <Box
      onDragEnter={(e) => { e.preventDefault(); if (onUpload) setDragOver(true); }}
      onDragOver={(e) => { e.preventDefault(); if (onUpload) setDragOver(true); }}
      onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
      onDrop={handleDrop}
      sx={{
        position: 'relative',
        borderRadius: 1,
        outline: dragOver ? (theme) => `2px dashed ${theme.palette.primary.main}` : 'none',
        transition: 'outline-color 120ms',
      }}
    >
      <TextField
        inputRef={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? t('productExamples.edit.contentPlaceholder')}
        label={label}
        multiline
        minRows={minRows}
        fullWidth
        disabled={disabled}
        slotProps={{
          htmlInput: {
            onPaste: handlePaste,
            style: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 13, lineHeight: 1.5 },
          },
        }}
      />
      {(uploading || dragOver) && (
        <Box
          sx={{
            position: 'absolute', inset: 0,
            bgcolor: dragOver ? 'primary.main' : 'action.hover',
            opacity: dragOver ? 0.08 : 1,
            pointerEvents: 'none',
            borderRadius: 1,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'stretch',
          }}
        >
          {uploading && <LinearProgress sx={{ width: '100%', height: 3, borderRadius: 1 }}/>}
        </Box>
      )}
      {dragOver && (
        <Typography
          variant="body2"
          sx={{ position: 'absolute', top: 8, left: 12, color: 'primary.main', fontWeight: 600, pointerEvents: 'none' }}
        >
          {t('productExamples.edit.dropHere')}
        </Typography>
      )}
    </Box>
  );

  const previewPane = (
    <Paper
      variant="outlined"
      sx={{ p: 2, minHeight: minRows * 24 + 16, overflow: 'auto', bgcolor: 'background.paper' }}
    >
      {value.trim()
        ? <MarkdownRenderer exampleId={exampleId} attachments={attachments}>{value}</MarkdownRenderer>
        : <Typography variant="body2" color="text.disabled">{t('productExamples.edit.previewEmpty')}</Typography>}
    </Paper>
  );

  if (isSm) {
    return (
      <Box>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
          <Tab value="source" label={t('productExamples.edit.tabSource')}/>
          <Tab value="preview" label={t('productExamples.edit.tabPreview')}/>
        </Tabs>
        {tab === 'source' ? sourcePane : previewPane}
      </Box>
    );
  }

  return (
    <Stack direction="row" spacing={2} sx={{ alignItems: 'stretch' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {t('productExamples.edit.tabSource')}
        </Typography>
        {sourcePane}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {t('productExamples.edit.tabPreview')}
        </Typography>
        {previewPane}
      </Box>
    </Stack>
  );
};
